import { z } from 'zod'
// Аспекты синастрии — углы между планетами ДВУХ карт.
//
// Так совместимость считают в серьёзных сервисах: берут точные долготы планет и смотрят угол
// между ними. Соединение, секстиль, тригон — согласие; квадрат и оппозиция — напряжение. Чем
// точнее угол, тем сильнее работает аспект, поэтому у каждого аспекта есть орб (допуск) и
// сила падает по мере удаления от точного значения.
//
// До этого мы сравнивали ТОЛЬКО знаки, а точные градусы, которые и так считаются эфемеридами,
// выбрасывали. Знаки остались как опора (см. `compatScore.ts`), но теперь они не единственный
// источник числа — и разброс создают настоящие углы, а не хеш дат.
import type { CompatType } from '../enums.js'
import type { CompatPlanet } from './compatScore.js'

export const ASPECT_KIND_VALUES = [
  'conjunction',
  'sextile',
  'square',
  'trine',
  'opposition',
] as const
/**
 * Схема нужна контракту разбора карты: углы уходят на фронт и должны валидироваться, как
 * и всё остальное в ответе. Тип выведен из схемы, а не написан рядом, — иначе список значений
 * пришлось бы держать в двух местах.
 */
export const aspectKindSchema = z.enum(ASPECT_KIND_VALUES)
export type AspectKind = z.infer<typeof aspectKindSchema>

/**
 * Точка карты, между которыми ищутся углы. Пять личных планет — всегда; асцендент — только
 * когда у ОБОИХ известны дата, время и город (`canUseAscendant`). Он не планета: это точка
 * восходящего горизонта, и без точного момента и координат её не существует.
 */
export type CompatPoint = CompatPlanet | 'asc'

/** Долготы точек карты. Асцендент необязателен — его чаще нет, чем есть. */
export type CompatPointPositions = Partial<Record<CompatPoint, number>>

/** Веса точек в конкретной линзе. Точка без веса в расчёт углов не идёт. */
export type CompatPointWeights = Partial<Record<CompatPoint, number>>

export interface AspectDefinition {
  /** Точный угол аспекта в градусах. */
  angle: number
  /** Орб — допуск, внутри которого аспект считается действующим. */
  orb: number
  /**
   * Гармония 0..1: насколько аспект «лёгкий». Тригон — самый лёгкий, квадрат — самый трудный.
   * Соединение чуть ниже тригона (слияние бывает и удушающим), оппозиция — притяжение через
   * напряжение, поэтому она посередине, а не внизу.
   */
  harmony: number
}

/** Классический набор мажорных аспектов с орбами, принятыми в синастрии. */
export const ASPECTS: Record<AspectKind, AspectDefinition> = {
  conjunction: { angle: 0, orb: 8, harmony: 0.85 },
  opposition: { angle: 180, orb: 7, harmony: 0.45 },
  trine: { angle: 120, orb: 7, harmony: 1 },
  square: { angle: 90, orb: 6, harmony: 0.25 },
  sextile: { angle: 60, orb: 4, harmony: 0.8 },
}

/**
 * Границы, в которых вообще может оказаться компонента углов.
 *
 * Считаются из таблицы `ASPECTS`, а не вписаны числами: поправят гармонию квадрата — границы
 * поедут сами. Нужны они скорингу (`compatScore.ts`), чтобы перевести компоненту в 0..1:
 * без этого верх шкалы недостижим, потому что «одни сплошные тригоны» дают не 1, а 1.0
 * гармонии тригона, и проценту неоткуда взять сотню.
 */
export const ASPECT_SCORE_BOUNDS = {
  min: Math.min(...Object.values(ASPECTS).map((aspect) => aspect.harmony)),
  max: Math.max(...Object.values(ASPECTS).map((aspect) => aspect.harmony)),
}

const ASPECT_ORDER: readonly AspectKind[] = [
  'conjunction',
  'opposition',
  'trine',
  'square',
  'sextile',
]

/** Нейтральное значение: пара планет без аспекта не говорит ни за, ни против. */
const NEUTRAL = 0.5

/**
 * Сглаживание: пара с одним-единственным точным тригоном не должна давать 100%.
 * Работает как «виртуальная нейтральная пара» веса `SMOOTHING` в знаменателе среднего.
 */
const SMOOTHING = 0.5

/** Угол между двумя долготами, приведённый к 0..180. */
export const angleBetween = (a: number, b: number): number => {
  const diff = Math.abs(((a - b) % 360) + 360) % 360
  return diff > 180 ? 360 - diff : diff
}

export interface AspectHit {
  kind: AspectKind
  /** 0..1: 1 — точный аспект, 0 — на самом краю орба. */
  exactness: number
  /** Отклонение от точного угла в градусах. */
  offset: number
}

/**
 * Аспект между двумя долготами, если он есть. При пересечении орбов (например 8° от соединения
 * и 4° от секстиля не пересекаются, но у широких орбов такое возможно) побеждает более точный.
 */
export const findAspect = (longitudeA: number, longitudeB: number): AspectHit | null => {
  const angle = angleBetween(longitudeA, longitudeB)
  let best: AspectHit | null = null

  for (const kind of ASPECT_ORDER) {
    const { angle: exact, orb } = ASPECTS[kind]
    const offset = Math.abs(angle - exact)
    if (offset > orb) continue

    const exactness = 1 - offset / orb
    if (!best || exactness > best.exactness) best = { kind, exactness, offset }
  }

  return best
}

/**
 * Вклад одной пары планет 0..1. Аспект тянет от нейтрали к своей гармонии тем сильнее, чем он
 * точнее: широкий тригон почти нейтрален, точный — максимален.
 */
export const aspectPairScore = (hit: AspectHit | null): number => {
  if (!hit) return NEUTRAL
  return NEUTRAL + (ASPECTS[hit.kind].harmony - NEUTRAL) * hit.exactness
}

export interface PairAspect {
  /** Точка карты того, кто считает. */
  own: CompatPoint
  /** Точка карты второго. */
  target: CompatPoint
  hit: AspectHit
  score: number
}

/**
 * Точки, которые есть в обеих картах. Асцендент считается только когда он известен у обоих:
 * иначе процент зависел бы от того, чьи данные полнее, а не от самой пары.
 */
const commonPoints = (own: CompatPointPositions, target: CompatPointPositions): CompatPoint[] =>
  (Object.keys(own) as CompatPoint[]).filter(
    (point) => own[point] !== undefined && target[point] !== undefined,
  )

/** Все аспекты между двумя наборами позиций — 25 пар (36 с асцендентом), аспект есть не у всех. */
export const findPairAspects = (
  own: CompatPointPositions,
  target: CompatPointPositions,
): PairAspect[] => {
  const points = commonPoints(own, target)
  const found: PairAspect[] = []

  for (const ownPoint of points) {
    for (const targetPoint of points) {
      const hit = findAspect(own[ownPoint] as number, target[targetPoint] as number)
      if (!hit) continue
      found.push({ own: ownPoint, target: targetPoint, hit, score: aspectPairScore(hit) })
    }
  }

  return found
}

/**
 * Пары планет, отвечающие за конкретную сферу линзы. Классическая логика: чувства — Луна и
 * Венера, страсть — Венера и Марс, разговоры — Меркурий, соперничество — Марс, границы —
 * Марс к Луне. Порядок пары важен: `[своя планета, планета второго]`.
 */
/**
 * Ключ сферы первого впечатления. Она есть у каждой линзы и считается ТОЛЬКО когда дата,
 * время и город известны у обоих: без них асцендента не существует.
 *
 * Заведена, потому что асцендент попал в неудобное положение — он весил в общем проценте
 * до 0.20, больше Луны у половинки, и при этом не показывался человеку нигде. Общий процент
 * проседал, а объяснить это было нечем. Теперь целое собирается из частей, которые видно.
 */
export const FIRST_IMPRESSION_SPHERE = 'first_impression'

/**
 * ПАРЫ ЗДЕСЬ ОБЯЗАНЫ БЫТЬ ЗЕРКАЛЬНЫМИ.
 *
 * Пара направленная: `['sun', 'mercury']` — это Солнце ТОГО, КТО СЧИТАЕТ, к Меркурию второго.
 * В синастрии направление осмысленно, но у каждой такой связки должна стоять и обратная,
 * иначе итог зависит от того, кто нажал кнопку. Так и было у семи линз из десяти: две подруги
 * проверяли друг друга по одной и той же линзе и видели 66% и 60%, а у коллеги расхождение
 * доходило до 28 пунктов. Симметрию держит тест в `compatScore.test.ts`, и он же называет
 * пару, у которой не хватило зеркала.
 *
 * У начальника и краша роли в жизни неравные, и направленность там была не случайной. Но
 * число, которое скачет от того, кто считает, объяснить человеку нельзя, поэтому зеркала
 * дописаны и им (решение основателя).
 */
const SPHERE_PAIRS: Record<
  CompatType,
  Record<string, ReadonlyArray<[CompatPoint, CompatPoint]>>
> = {
  partner: {
    feelings: [
      ['moon', 'moon'],
      ['moon', 'venus'],
      ['venus', 'moon'],
    ],
    passion: [
      ['venus', 'mars'],
      ['mars', 'venus'],
      ['mars', 'mars'],
    ],
    daily_life: [
      ['sun', 'moon'],
      ['moon', 'sun'],
      ['sun', 'sun'],
    ],
    conversations: [
      ['mercury', 'mercury'],
      ['mercury', 'moon'],
      ['moon', 'mercury'],
    ],
  },
  friend: {
    why_clicked: [
      ['sun', 'sun'],
      ['mercury', 'mercury'],
      ['sun', 'mercury'],
      ['mercury', 'sun'],
    ],
    who_annoys: [
      ['mars', 'mars'],
      ['mars', 'mercury'],
      ['moon', 'mars'],
      ['mercury', 'mars'],
      ['mars', 'moon'],
    ],
    general_vibe: [
      ['moon', 'moon'],
      ['venus', 'venus'],
      ['sun', 'moon'],
      ['moon', 'sun'],
    ],
  },
  crush: {
    is_there_a_chance: [
      ['venus', 'mars'],
      ['mars', 'venus'],
      ['sun', 'venus'],
      ['venus', 'sun'],
    ],
    how_he_works: [
      ['sun', 'sun'],
      ['mars', 'sun'],
      ['mercury', 'mars'],
      ['sun', 'mars'],
      ['mars', 'mercury'],
    ],
    when_to_approach: [
      ['moon', 'venus'],
      ['venus', 'moon'],
      ['mercury', 'venus'],
      ['venus', 'mercury'],
    ],
  },
  ex: {
    why_it_didnt_work: [
      ['mars', 'mars'],
      ['moon', 'mars'],
      ['mars', 'moon'],
    ],
    what_it_was: [
      ['venus', 'mars'],
      ['mars', 'venus'],
      ['moon', 'moon'],
    ],
  },
  colleague: {
    work_together: [
      ['mercury', 'mercury'],
      ['sun', 'mercury'],
      ['mars', 'mars'],
      ['mercury', 'sun'],
    ],
    who_annoys_on_calls: [
      ['mars', 'mercury'],
      ['mercury', 'mars'],
      ['moon', 'mars'],
      ['mars', 'moon'],
    ],
  },
  boss: {
    how_reads_you: [
      ['sun', 'sun'],
      ['sun', 'mercury'],
      ['mercury', 'sun'],
    ],
    when_to_ask_raise: [
      ['sun', 'venus'],
      ['venus', 'sun'],
      ['mercury', 'venus'],
      ['venus', 'mercury'],
    ],
    when_to_avoid: [
      ['mars', 'sun'],
      ['sun', 'mars'],
      ['mars', 'mars'],
    ],
  },
  mother: {
    understanding: [
      ['moon', 'moon'],
      ['mercury', 'moon'],
      ['moon', 'mercury'],
    ],
    boundaries: [
      ['mars', 'moon'],
      ['moon', 'mars'],
      ['sun', 'mars'],
      ['mars', 'sun'],
    ],
    warmth: [
      ['venus', 'moon'],
      ['moon', 'venus'],
      ['venus', 'venus'],
    ],
  },
  father: {
    understanding: [
      ['sun', 'mercury'],
      ['mercury', 'sun'],
      ['mercury', 'mercury'],
    ],
    respect: [
      ['sun', 'sun'],
      ['mars', 'sun'],
      ['sun', 'mars'],
    ],
    unspoken: [
      ['moon', 'moon'],
      ['moon', 'mercury'],
      ['venus', 'moon'],
      ['mercury', 'moon'],
      ['moon', 'venus'],
    ],
  },
  sibling: {
    shared_language: [
      ['mercury', 'mercury'],
      ['moon', 'mercury'],
      ['mercury', 'moon'],
    ],
    rivalry: [
      ['mars', 'mars'],
      ['sun', 'mars'],
      ['mars', 'sun'],
    ],
    support: [
      ['moon', 'moon'],
      ['venus', 'moon'],
      ['moon', 'venus'],
    ],
  },
  relative: {
    common_ground: [
      ['sun', 'sun'],
      ['moon', 'moon'],
      ['mercury', 'mercury'],
    ],
    friction: [
      ['mars', 'mars'],
      ['mars', 'mercury'],
      ['moon', 'mars'],
      ['mercury', 'mars'],
      ['mars', 'moon'],
    ],
  },
}

/**
 * Пары сферы первого впечатления: асцендент против асцендента и асцендент против светил.
 * Классическая синастрия читает эти связки как «узнаёт ли человек себя рядом со вторым»
 * и «что считывается до знакомства».
 */
const FIRST_IMPRESSION_PAIRS: ReadonlyArray<[CompatPoint, CompatPoint]> = [
  ['asc', 'asc'],
  ['asc', 'sun'],
  ['sun', 'asc'],
  ['asc', 'moon'],
  ['moon', 'asc'],
]

export const getSpherePairs = (
  type: CompatType,
  sphereKey: string,
): ReadonlyArray<[CompatPoint, CompatPoint]> =>
  sphereKey === FIRST_IMPRESSION_SPHERE
    ? FIRST_IMPRESSION_PAIRS
    : (SPHERE_PAIRS[type][sphereKey] ?? [])

/**
 * Средний вклад набора пар со сглаживанием: одна точная связь не выкручивает сферу в потолок,
 * а полное отсутствие аспектов оставляет её на нейтрали.
 */
export const averagePairScores = (scores: readonly number[]): number => {
  if (scores.length === 0) return NEUTRAL
  const sum = scores.reduce((total, score) => total + score, 0) + NEUTRAL * SMOOTHING
  return sum / (scores.length + SMOOTHING)
}

/**
 * Аспектная составляющая общего процента: те же пары, что и в сферах линзы, плюс аспекты
 * между одноимёнными планетами. Считается только по парам, у которых аспект действительно есть,
 * — пары без аспекта не голосуют, но сглаживание не даёт единственной связи решить всё.
 */
/**
 * Вес пары точек, где единица — пара средней важности.
 *
 * Сам вес — произведение важностей двух точек в этой линзе: Венера–Марс у половинки значит
 * куда больше, чем Меркурий–Меркурий, а у коллег наоборот. Но важности линзы в сумме дают
 * единицу, поэтому сырое произведение — величина порядка 1/n², и соизмерять её со сглаживанием
 * нельзя. Домножение на n² переводит вес в понятную единицу: пара из двух средних по важности
 * точек весит ровно 1, вдвое более важная — 2. Ранжирование пар между собой от этого не
 * меняется, меняется только масштаб, в котором с ними сравнивается `SMOOTHING`.
 */
const pairWeight = (weights: CompatPointWeights, own: CompatPoint, target: CompatPoint): number => {
  const count = Object.keys(weights).length
  return (weights[own] ?? 0) * (weights[target] ?? 0) * count * count
}

export const calcAspectComponent = (
  own: CompatPointPositions,
  target: CompatPointPositions,
  weights: CompatPointWeights,
): number => {
  const hits = findPairAspects(own, target)
  if (hits.length === 0) return NEUTRAL

  let weighted = NEUTRAL * SMOOTHING
  let total = SMOOTHING

  for (const hit of hits) {
    const weight = pairWeight(weights, hit.own, hit.target)
    if (weight === 0) continue
    weighted += hit.score * weight
    total += weight
  }

  return weighted / total
}
